import React, { useEffect, useMemo, useState } from "react";
import WelcomeBanner from "../partials/dashboard/WelcomeBanner";
import DashboardCard01 from "../partials/dashboard/DashboardCard01";
import DashboardCard02 from "../partials/dashboard/DashboardCard02";
import DashboardCard03 from "../partials/dashboard/DashboardCard03";
import DashboardCard04 from "../partials/dashboard/DashboardCard04";
import DashboardCard07 from "../partials/dashboard/DashboardCard07";
import { useSelector } from "react-redux";
import { getAnalytics, getPDFAnalytics } from "../api/index.js";
import moment from "moment";
import ToggleSwitch from "../components/ToggleSwitch.jsx";
import DateSelect from "../components/DateSelect.jsx";
import SalesReport from "../partials/salesReport/SalesReport.jsx";
import ModalBasic from "../components/ModalBasic.jsx";
import { PDFViewer } from "@react-pdf/renderer";
import { PrimaryButton } from "../components/Buttons.jsx";

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [unit, setUnit] = useState("day");
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(
    moment().subtract(7, "days").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [pdf, setPDF] = useState("locked");

  const [aggregated, setAggregated] = useState(false);
  const { selectedLocation, selectedMerchant } = useSelector(
    (state) => state.app
  );

  useEffect(() => {
    if (!selectedLocation || !selectedMerchant) {
      setPDF({ status: "locked" });
    } else {
      setPDF({ status: "not_started" });
    }
  }, [selectedLocation, selectedMerchant, startDate, endDate, aggregated]);

  useEffect(() => {
    if (!selectedLocation) {
      return;
    }
    setLoading(true);
    getAnalytics(startDate, endDate, aggregated).then((res) => {
      setStats(res.analytics);
      setUnit(res.unit);
      setLoading(false);
    });
  }, [startDate, endDate, selectedLocation, aggregated]);

  const sales = useMemo(() => {
    const data = { labels: [], data: [], unit: "day", total: 0 };
    for (let i = 0; i < (stats || []).length; i++) {
      const date = stats[i].date.split("T")[0].split("-")
      data.labels.push(`${date[1]}-${date[2]}-${date[0]}`);
      data.data.push(
        (stats[i].new_customer_revenue || 0) +
          (stats[i].returning_customer_revenue || 0)
      );
      data.total += data.data[i];
    }
    return data;
  }, [stats]);

  const orders = useMemo(() => {
    const data = { labels: [], data: [], unit: "day", total: 0 };
    for (let i = 0; i < (stats || []).length; i++) {
      const date = stats[i].date.split("T")[0].split("-")
      data.labels.push(`${date[1]}-${date[2]}-${date[0]}`);
      data.data.push(stats[i].order_count || 0);
      data.total += data.data[i];
    }
    return data;
  }, [stats]);

  const aov = useMemo(() => {
    const data = { labels: [], data: [], unit: "day", total: 0 };
    let orders = 0;
    for (let i = 0; i < (stats || []).length; i++) {
      const date = stats[i].date.split("T")[0].split("-")
      data.labels.push(`${date[1]}-${date[2]}-${date[0]}`);
      data.data.push(stats[i].aov || 0);
      data.total +=
        (stats[i].new_customer_revenue || 0) +
        (stats[i].returning_customer_revenue || 0);
      orders += stats[i].order_count || 0;
    }
    data.total /= Math.max(orders, 1);
    return data;
  }, [stats]);

  const revenueBrokenDown = useMemo(() => {
    const data = {
      labels: [],
      newData: [],
      returningData: [],
      newTotal: 0,
      returningTotal: 0,
      unit: "day",
    };
    for (let i = 0; i < (stats || []).length; i++) {
      const date = stats[i].date.split("T")[0].split("-")
      data.labels.push(`${date[1]}-${date[2]}-${date[0]}`);
      data.newData.push(stats[i].new_customer_revenue || 0);
      data.returningData.push(stats[i].returning_customer_revenue || 0);
      data.newTotal += data.newData[i];
      data.returningTotal += data.returningData[i];
    }
    return data;
  }, [stats]);

  const isIncomplete = useMemo(() => {
    return endDate === moment(new Date()).format("YYYY-MM-DD");
  }, [endDate]);

  const productData = useMemo(() => {
    let data = {};
    for (let i = 0; i < (stats || []).length; i++) {
      Object.keys(stats[i].products || {}).forEach((skuID) => {
        if (!data[skuID]) {
          data[skuID] = {
            qty: stats[i].products[skuID].qty,
            revenue: stats[i].products[skuID].revenue,
            image: stats[i].products[skuID].image,
            title: stats[i].products[skuID].title,
            subtitle: stats[i].products[skuID].subtitle,
            brand: stats[i].products[skuID].brand,
            volume: stats[i].products[skuID].volume,
          };
        } else {
          data[skuID].qty += stats[i].products[skuID].qty;
          data[skuID].revenue += stats[i].products[skuID].revenue;
        }
      });
    }
    data = Object.values(data);
    data.sort((a, b) => b.revenue - a.revenue);
    data = data.slice(0, 10);
    return data;
  }, [stats]);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
      {/* Welcome banner */}
      <WelcomeBanner aggregated={aggregated} />

      {/* Dashboard actions */}
      <div className="sm:flex sm:justify-between sm:items-center mb-8 gap-2">
        {/* Right: Actions */}
        <div className="grid grid-flow-col sm:auto-cols-max justify-start sm:justify-end gap-2 flex-1">
          {/* Filter button */}
          {/*<FilterButton align="right" />*/}
          {/* Datepicker built with flatpickr */}
          {selectedMerchant?.locations?.length > 1 && (
            <ToggleSwitch
              checked={aggregated}
              onChange={(value) => {
                setAggregated(value);
              }}
              onLabel={"Aggregate all locations"}
              offLabel={"Aggregate all locations"}
            />
          )}
          <DateSelect
            onSelect={({ start, end }) => {
              setStartDate(start);
              setEndDate(end);
            }}
          />
        </div>

      </div>

      {/* Cards */}
      <div className="grid grid-cols-12 gap-6">
        {/* Line chart (Acme Plus) */}
        <DashboardCard01
          labels={sales.labels}
          data={sales.data}
          unit={unit}
          total={sales.total}
          loading={loading}
          isIncomplete={isIncomplete}
        />
        {/* Line chart (Acme Advanced) */}
        <DashboardCard02
          labels={orders.labels}
          data={orders.data}
          unit={unit}
          total={orders.total}
          loading={loading}
          isIncomplete={isIncomplete}
        />
        {/* Line chart (Acme Professional) */}
        <DashboardCard03
          labels={aov.labels}
          data={aov.data}
          unit={unit}
          total={aov.total}
          loading={loading}
          isIncomplete={isIncomplete}
        />{" "}
        {/* Bar chart (Direct vs Indirect) */}
        <DashboardCard04
          labels={revenueBrokenDown.labels}
          unit={unit}
          newData={revenueBrokenDown.newData}
          returningData={revenueBrokenDown.returningData}
          newTotal={revenueBrokenDown.newTotal}
          returningTotal={revenueBrokenDown.returningTotal}
          loading={loading}
        />
        {/* Line chart (Real Time Value) */}
        <DashboardCard07 data={productData} />
        {/* Doughnut chart (Top Countries) */}
      </div>
      <ModalBasic
        modalOpen={pdf.status === "open"}
        setModalOpen={() => {
          setPDF({ status: "ready", data: pdf.data });
        }}
        title={"Sales Report"}
      >
        <div className={"w-full h-[1152px]"}>
          {pdf.status === "open" && (
            <PDFViewer style={{ width: "100%", height: "100%" }}>
              <SalesReport
                data={pdf.data}
                startDate={startDate}
                endDate={endDate}
                isAggregate={aggregated}
                selectedLocation={selectedLocation}
                selectedMerchant={selectedMerchant}
              />
            </PDFViewer>
          )}
        </div>
      </ModalBasic>
    </div>
  );
}

export default Dashboard;
